import { useState } from 'react';
import { useDatos } from '../../datos/DatosContext';
import { useAviso, useRetardo } from '../../lib/avisos';
import { formatearFecha } from '../../lib/fechas';
import { borradorSueno } from '../../datos/simulaciones';
import { Icono } from '../../componentes/ui/Icono';
import { Bloqueado, Demo, Pensando, Volver } from '../../componentes/ui/basicos';
import { EtiquetasSueno } from './Suenos';
import { NoEncontrado } from '../NoEncontrado';

type EstadoIA = { estado: 'inactivo' } | { estado: 'cargando' } | { estado: 'borrador'; texto: string };

export function SuenoDetalle({ id }: { id: string }) {
  const { datos, actualizar } = useDatos();
  const aviso = useAviso();
  const retardo = useRetardo();
  const [ia, setIA] = useState<EstadoIA>({ estado: 'inactivo' });
  const sueno = datos.suenos.find((d) => d.id === id);
  if (!sueno) return <NoEncontrado />;

  const pedir = () => {
    setIA({ estado: 'cargando' });
    retardo(() => setIA({ estado: 'borrador', texto: borradorSueno(sueno) }), 1400);
  };

  return (
    <div className="stack-lg">
      <Volver a="/suenos">Sueños</Volver>
      <header>
        <p className="eyebrow">{formatearFecha(sueno.fecha)}</p>
        <h1 className="display">{sueno.titulo}</h1>
        <div className="row" style={{ marginTop: 12 }}><EtiquetasSueno sueno={sueno} /></div>
      </header>

      <section className="card stack-sm">
        <div className="between">
          <h2 className="eyebrow">Relato original</h2>
          <span className="tag"><Icono nombre="lock" tam="sm" />Texto original · no se modifica</span>
        </div>
        <p className="prose reading" style={{ fontSize: 19, lineHeight: 1.65, margin: '4px 0 0' }}>{sueno.relato}</p>
        <div>
          <button className="btn quiet small" onClick={() => aviso('Solo visual: las correcciones se guardarán aparte, sin tocar el relato original.')}>
            Añadir una corrección aparte <Demo />
          </button>
        </div>
      </section>

      <section className="card stack-sm">
        <label className="eyebrow" htmlFor="refl">Mi reflexión</label>
        <textarea id="refl" rows={3} placeholder="¿Qué te dice a ti este sueño?" value={sueno.reflexionPropia} onChange={(e) => actualizar('suenos', sueno.id, { reflexionPropia: e.target.value })} />
        <p className="muted small" style={{ margin: 0 }}>Tu texto es tuyo: la IA nunca lo sustituye.</p>
      </section>

      <section className="stack-sm">
        <div className="between">
          <h2 className="eyebrow">Reflexión con IA</h2>
          <button className="btn" aria-pressed={sueno.permitirIA} onClick={() => { actualizar('suenos', sueno.id, { permitirIA: !sueno.permitirIA }); setIA({ estado: 'inactivo' }); }}>
            <Icono nombre={sueno.permitirIA ? 'unlock' : 'lock'} tam="sm" />
            {sueno.permitirIA ? 'Consulta IA: permitida' : 'Consulta IA: no permitida'}
          </button>
        </div>

        {sueno.reflexionIAAprobada ? (
          <div className="card dusk stack-sm">
            <span className="eyebrow" style={{ color: 'var(--dusk)' }}>Reflexión IA aprobada por ti</span>
            <p className="prose" style={{ margin: 0 }}>{sueno.reflexionIAAprobada}</p>
          </div>
        ) : null}

        {!sueno.permitirIA ? (
          <Bloqueado titulo="Este sueño no está autorizado para la IA.">
            En la app real, el servidor rechazará la consulta aunque alguien la pida desde la pantalla. Aquí solo se muestra el comportamiento.
          </Bloqueado>
        ) : null}

        {sueno.permitirIA && ia.estado === 'inactivo' ? (
          <div><button className="btn primary" onClick={pedir}><Icono nombre="spark" />Pedir una reflexión</button> <Demo>Respuesta simulada</Demo></div>
        ) : null}
        {ia.estado === 'cargando' ? <Pensando>Preparando un borrador con este sueño…</Pensando> : null}
        {ia.estado === 'borrador' ? (
          <div className="draft stack-sm">
            <div className="between"><span className="tag dusk">Borrador de IA · no guardado</span><Demo>Simulado</Demo></div>
            <p className="prose" style={{ margin: '4px 0 0' }}>{ia.texto}</p>
            <p className="small muted" style={{ margin: 0 }}>Basado en: el relato original y tus etiquetas de este sueño. No he usado otros sueños.</p>
            <div className="row">
              <button className="btn primary" onClick={() => { actualizar('suenos', sueno.id, { reflexionIAAprobada: ia.texto }); setIA({ estado: 'inactivo' }); aviso('Reflexión aprobada y guardada aparte del relato.'); }}>
                <Icono nombre="check" tam="sm" />Aprobar y guardar
              </button>
              <button className="btn" onClick={() => { setIA({ estado: 'inactivo' }); aviso('Borrador descartado. No se ha guardado nada.'); }}>Descartar</button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
